import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { PriceCurvesPlotLabels } from '@digital-twins/deck-ui/plots/price-curves'

/**
 * The `price-curves` chrome in the viewer's UI language (plots plan G, owner
 * decision 5), keyed by the plot's label keys. Stored summary cards and the
 * analysis output's `priceCurve` carry no chrome, so the app supplies the same
 * keys `deck_labels.py` holds for the deck, from its own translations.
 */
export function usePriceCurvesLabels(): PriceCurvesPlotLabels {
  const { t } = useTranslation('app')

  return useMemo(
    () => ({
      price_role_too_cheap: t('studyReport.priceRoleTooCheap'),
      price_role_cheap: t('studyReport.priceRoleCheap'),
      price_role_expensive: t('studyReport.priceRoleExpensive'),
      price_role_too_expensive: t('studyReport.priceRoleTooExpensive'),
      price_sensitivity_range_label: t('studyReport.acceptableRange'),
      price_sensitivity_range_separator: t('studyReport.priceRangeSeparator'),
      price_sensitivity_optimum_label: t('studyReport.optimalPricePoint'),
      price_sensitivity_point_withheld: t('studyReport.priceOptimumWithheld'),
      price_sensitivity_lower_endpoint: t('studyReport.lowerAcceptablePrice'),
      price_sensitivity_upper_endpoint: t('studyReport.upperAcceptablePrice'),
      price_sensitivity_reference_label: t('studyReport.typicalCurrentSpend'),
      price_sensitivity_y_axis_title: t('studyReport.priceShareAxisTitle'),
      price_sensitivity_x_axis_title_fallback: t('studyReport.priceAxisTitleFallback'),
      price_sensitivity_method_note: t('studyReport.priceMethodNote'),
      plot_price_no_currency: t('studyReport.noCurrency'),
      plot_off_scale_note: t('studyReport.priceOffScaleNote'),
    }),
    [t],
  )
}

/**
 * The base sentence under the curve, one whole translated sentence per shape
 * (who answered, never who was asked). `baseSize` is the base used after the
 * ordering filter, as on the deck slide.
 */
export function usePriceCurveBaseLine(curve: {
  baseSize: number
  respondentCount?: number | null
  reference?: { baseSize: number } | null
}): string | null {
  const { t } = useTranslation('app')

  if (curve.respondentCount == null) {
    return null
  }
  if (curve.reference != null) {
    return t('studyReport.priceCurveBaseWithReference', {
      base: curve.baseSize,
      respondents: curve.respondentCount,
      referenceBase: curve.reference.baseSize,
    })
  }
  return t('studyReport.priceCurveBase', { base: curve.baseSize, respondents: curve.respondentCount })
}

/** App i18n key per canonical terminal `price_curve_*` refusal code. */
const PRICE_CURVE_REFUSAL_KEYS = {
  price_curve_mixed_currency: 'studyReport.priceCurveRefusedMixedCurrency',
  price_curve_insufficient_base: 'studyReport.priceCurveRefusedInsufficientBase',
  price_curve_insufficient_price_knots: 'studyReport.priceCurveRefusedPriceKnots',
  price_curve_role_order_systematic: 'studyReport.priceCurveRefusedRoleOrder',
  price_curve_missing_intersections: 'studyReport.priceCurveRefusedMissingIntersections',
  price_curve_invalid_geometry: 'studyReport.priceCurveRefusedInvalidGeometry',
  price_curve_unavailable: 'studyReport.priceCurveRefusedUnavailable',
} as const

function isRefusalCode(code: string): code is keyof typeof PRICE_CURVE_REFUSAL_KEYS {
  return Object.prototype.hasOwnProperty.call(PRICE_CURVE_REFUSAL_KEYS, code)
}

/**
 * Why a price curve was refused, in the viewer's UI language, read from the
 * producer's typed `code`. The copy stamped in the run language (`message`) is
 * the fallback for a code this app does not know yet.
 */
export function usePriceCurveRefusalText(refusal: { code: string; message: string } | null | undefined): string | null {
  const { t } = useTranslation('app')

  if (refusal == null) {
    return null
  }
  if (isRefusalCode(refusal.code)) {
    return t(PRICE_CURVE_REFUSAL_KEYS[refusal.code])
  }
  return refusal.message || null
}
